import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { MaintenanceList } from "@/components/MaintenanceList";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", brand: "", model: "", serial: "", notes: "", hour_meter: null, hour_meter_date: "", carburetor: "", ignition: "", engine_setup: "", maintenance: [] };

const EngineFields = ({ form, set, setForm }) => {
  const { t } = useT();
  return (
    <>
      <Field label={t("hour_meter")}><Input data-testid="engine-hour-meter-input" type="number" min="0" step="0.1" value={form.hour_meter ?? ""} onChange={(e) => setForm({ ...form, hour_meter: e.target.value === "" ? null : Number(e.target.value) })} className={fieldInputCls} /></Field>
      <Field label={t("hour_meter_date")}><Input data-testid="engine-hour-meter-date-input" type="date" value={form.hour_meter_date || ""} onChange={set("hour_meter_date")} className={`${fieldInputCls} [color-scheme:dark]`} /></Field>
      <Field label={t("carburetor")}><Input data-testid="engine-carburetor-input" value={form.carburetor || ""} onChange={set("carburetor")} className={fieldInputCls} /></Field>
      <Field label={t("ignition")}><Input data-testid="engine-ignition-input" value={form.ignition || ""} onChange={set("ignition")} className={fieldInputCls} /></Field>
      <Field label={t("engine_setup")} className="sm:col-span-2">
        <Textarea data-testid="engine-setup-input" rows={3} value={form.engine_setup || ""} onChange={set("engine_setup")} className={`${fieldInputCls} h-auto font-sans`} />
      </Field>
      <div className="sm:col-span-2"><MaintenanceList rows={form.maintenance || []} onChange={(rows) => setForm({ ...form, maintenance: rows })} /></div>
    </>
  );
};

export const GarageItemDialog = ({ open, onOpenChange, kind, item, onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const engine = kind === "engines";
  useEffect(() => {
    if (open) {
      setForm(item ? { ...EMPTY, ...item } : EMPTY);
    }
  }, [open, item]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]]));
    try {
      if (item) await api.put(`/garage/${kind}/${item.id}`, payload);
      else await api.post(`/garage/${kind}`, payload);
      toast.success(t("saved"));
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="garage-item-dialog" className={`bg-[#12141C] border-[#282D3D] rounded-sm ${engine ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">
            {item ? t("edit_item") : t("new_item")} · {engine ? t("engine") : t("chassis")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={`${t("name")} *`} className="sm:col-span-2">
            <Input data-testid="garage-name-input" required value={form.name} onChange={set("name")} className={fieldInputCls} />
          </Field>
          <Field label={t("brand")}><Input data-testid="garage-brand-input" value={form.brand} onChange={set("brand")} className={fieldInputCls} /></Field>
          <Field label={t("model")}><Input data-testid="garage-model-input" value={form.model} onChange={set("model")} className={fieldInputCls} /></Field>
          <Field label={t("serial")} className="sm:col-span-2"><Input data-testid="garage-serial-input" value={form.serial} onChange={set("serial")} className={fieldInputCls} /></Field>
          {engine && <EngineFields form={form} set={set} setForm={setForm} />}
          <Field label={t("notes")} className="sm:col-span-2">
            <Textarea data-testid="garage-notes-input" rows={2} value={form.notes} onChange={set("notes")} className={`${fieldInputCls} h-auto font-sans`} />
          </Field>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" data-testid="garage-cancel-btn" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" data-testid="garage-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base px-6">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
